import { useMutation, useQuery } from '@apollo/client/react';
import { ZonedDateTime } from '@js-joda/core';
import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { mergeSentMessage } from '../../apollo/merge';
import { SelfDocument, SendMessageDocument } from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import { useUploadFile, validateFile } from '../../hooks/useUploadFile';
import { EventCategories, EventNames } from '../../utils/trackingEvents';
import { useChatContext } from './Context';

const SOMETHING_WENT_WRONG =
  'Something went wrong. If the problem persists, please contact Coach Hub support.';

export function useSendMessage() {
  const { chatId } = useChatContext();
  const { track } = useTracking();
  const { data: selfData } = useQuery(SelfDocument);
  const loggedInUser = selfData?.self;
  const [error, setError] = useState<string | null>(null);
  const [sendMessage] = useMutation(SendMessageDocument);
  const send = React.useCallback(
    (textUntrimmed: string) => {
      setError(null);
      if (!chatId) {
        return Promise.resolve();
      }
      const text = textUntrimmed.trimStart().trimEnd();
      const optimisticId = `optimistic_${uuid()}`;
      track(EventNames.Send, {
        event_category: EventCategories.Chat,
        chat_id: chatId,
      });
      return sendMessage({
        variables: { text, chatId, optimisticId },
        optimisticResponse: loggedInUser
          ? {
              sendMessage: {
                __typename: 'Message',
                id: optimisticId,
                text,
                createdAt: ZonedDateTime.now().toString(),
                user: loggedInUser,
                automated: false,
                reactions: [],
                previews: [],
              },
            }
          : undefined,
        update: (cache, result) => {
          cache.modify({
            id: cache.identify({
              id: chatId,
              __typename: 'ChatRoom',
            }),
            fields: {
              messages: (existingMessages) =>
                mergeSentMessage(cache, existingMessages, result),
            },
          });
        },
        onError: () => {
          setError(SOMETHING_WENT_WRONG);
          throw new Error(textUntrimmed);
        },
      });
    },
    [sendMessage, chatId, loggedInUser, track],
  );

  const { uploadFile } = useUploadFile();
  const validateFileInput = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): File | null => {
    setError(null);
    const files = e.target.files ?? [];
    const file = files[0];
    if (!file) {
      return null;
    }
    const fileValidation = validateFile(file);
    if (!fileValidation.isValid) {
      setError(fileValidation.message);
      return null;
    }
    return file;
  };
  const sendFile = async (file: File) => {
    try {
      // need to upload image
      const { url } = await uploadFile(file);

      // then send URL
      return await send(url);
    } catch {
      setError(SOMETHING_WENT_WRONG);
      throw new Error('Failed to upload image');
    }
  };
  return {
    send,
    sendFile,
    validateFileInput,
    error,
    uploadFile,
  };
}
